import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { memoGeneric } from '@/js/libs/memo';
import type { Observable } from '@/js/libs/observable';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import { selectedFilterOptions } from 'src/components/TableFilters/helpers.ts';
import type { Option } from 'src/types.ts';
import type { FilterConfigs, FilterValue } from '../types.ts';

interface MultiFilterProps<TFilters extends Record<string, FilterValue>> {
  multiFilter$: Observable<Array<keyof TFilters>>;
  filterKeys: Array<keyof TFilters>;
  filterConfigs: FilterConfigs<TFilters>;
  setMultiFilter: (updater: Array<keyof TFilters> | ((prev: Array<keyof TFilters>) => Array<keyof TFilters>)) => void;
  removeFilter: (key: keyof TFilters) => void;
}

export const MultiFilter = memoGeneric(function MultiFilter<TFilters extends Record<string, FilterValue>>({
  multiFilter$,
  filterKeys,
  filterConfigs,
  setMultiFilter,
  removeFilter,
}: MultiFilterProps<TFilters>) {
  const [t] = useTranslation(['backend', 'common']);
  const multiFilter = useSubscribe(multiFilter$);

  const options = useMemo(
    () =>
      filterKeys
        .filter((key) => !filterConfigs[key].isMainFilter && !filterConfigs[key].hidden)
        .map((key) => ({
          label: String(key),
          value: key,
        })),
    [filterConfigs, filterKeys],
  );

  const selected = useMemo(() => selectedFilterOptions(options, multiFilter), [multiFilter, options]);

  const handleChange = useCallbackRef((selectedOptions: Option<keyof TFilters>[]) => {
    const selectedKeys = selectedOptions.map((option) => option.value);
    const removedFilters = multiFilter.filter((item) => !selectedKeys.includes(item));

    // Remove filters that are no longer selected
    removedFilters.forEach((item) => removeFilter(item));

    // Preserve existing order and add new selections to the end
    const newSelections = selectedKeys.filter((key) => !multiFilter.includes(key));
    const preservedOrder = multiFilter.filter((key) => selectedKeys.includes(key));

    setMultiFilter([...preservedOrder, ...newSelections]);
  });

  const formatOptionLabel = useCallbackRef((option: Option<keyof TFilters>) => {
    const filterKey = option.value as keyof TFilters;
    const config = filterConfigs[filterKey];

    if (config && !config.isMainFilter && 'getLabel' in config && config.getLabel) {
      return config.getLabel();
    }

    return String(filterKey);
  });

  if (options.length === 0) {
    return null;
  }

  return (
    <Filter<keyof TFilters>
      key='multi-filter'
      options={options}
      selected={selected}
      onChange={handleChange}
      formatOptionLabel={formatOptionLabel}
      isEnabledSelectedLabel={false}
      label={t('common:SELECT_FILTERS')}
      isMulti
      dataTestId='multi-filter'
    />
  );
});
